import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { withCallStatus } from '@ngrx-traits/signals';
import {
  patchState,
  signalStoreFeature,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AuthNavigationService } from '@seatix/shared/auth/utils';
import { injectIsFanPortal } from '@seatix/shared/core';
import { LoginCredentials, RegisterCredentials } from '@seatix/shared/domain';
import { catchError, EMPTY, finalize, pipe, switchMap, tap } from 'rxjs';
import { AuthApiService } from '../auth-api/auth-api.service';
import { withUserResource } from './with-user-resource';

export function withAuthentication() {
  return signalStoreFeature(
    withState<{ navigate: boolean }>({
      navigate: false,
    }),
    withUserResource(),
    withCallStatus({
      collection: 'login',
      errorType: 'string',
    }),
    withCallStatus({
      collection: 'register',
      errorType: 'string',
    }),
    withCallStatus({
      collection: 'socialLogin',
      errorType: 'string',
    }),
    withProps(() => ({
      _authApiService: inject(AuthApiService),
      _authNavigationService: inject(AuthNavigationService),
      _isFanPortal: injectIsFanPortal(),
      _router: inject(Router),
    })),
    withMethods((store) => {
      const isFanPortal = store._isFanPortal;
      const authNavigationService = store._authNavigationService;
      const router = store._router;

      function resetCallStatus() {
        patchState(store, {
          loginCallStatus: 'init',
          registerCallStatus: 'init',
          socialLoginCallStatus: 'init',
        });
      }
      function clearAuth() {
        store.clearUser();
        patchState(store, { current_team: null });
        resetCallStatus();
      }
      function logoutLocal() {
        clearAuth();
        authNavigationService.navigateAfterLogout();
      }
      function shouldNavigate() {
        patchState(store, { navigate: true });
      }
      function resetNavigationFlag() {
        patchState(store, { navigate: false });
      }
      function navigateAfterLogin() {
        if (!isFanPortal) {
          router.navigateByUrl('/');
        }
      }

      return {
        resetCallStatus,
        logoutLocal,
        clearAuth,
        shouldNavigate,
        resetNavigationFlag,
        navigateAfterLogin,
      };
    }),
    withMethods((store) => {
      const authApiService = store._authApiService;

      return {
        login: rxMethod<{
          credentials: LoginCredentials;
          turnstileToken: string;
        }>(
          pipe(
            switchMap(({ credentials, turnstileToken }) => {
              store.resetCallStatus();
              store.setLoginLoading();
              store.shouldNavigate();

              return authApiService.login(credentials, turnstileToken).pipe(
                tap((response) => {
                  store.setUser(response.data);
                  store.setLoginLoaded();
                }),
                catchError((error) => {
                  store.setLoginError(error.userMessage);
                  store.resetNavigationFlag();
                  return EMPTY;
                }),
              );
            }),
          ),
        ),

        register: rxMethod<{
          credentials: RegisterCredentials;
          turnstileToken: string;
        }>(
          pipe(
            switchMap(({ credentials, turnstileToken }) => {
              store.resetCallStatus();
              store.setRegisterLoading();
              store.shouldNavigate();

              return authApiService.register(credentials, turnstileToken).pipe(
                tap((response) => {
                  store.setUser(response.data);
                  store.setRegisterLoaded();
                }),
                catchError((error) => {
                  store.setRegisterError(error.userMessage);
                  store.resetNavigationFlag();
                  return EMPTY;
                }),
              );
            }),
          ),
        ),

        socialLogin: rxMethod<string>(
          pipe(
            switchMap((token) => {
              store.resetCallStatus();
              store.setSocialLoginLoading();
              store.shouldNavigate();

              return authApiService.handleGoogleCallback(token).pipe(
                tap((response) => {
                  store.setUser(response.data);
                  store.setSocialLoginLoaded();
                }),
                catchError((error) => {
                  store.setSocialLoginError(
                    error.userMessage || 'Social login failed',
                  );
                  store.resetNavigationFlag();
                  return EMPTY;
                }),
              );
            }),
          ),
        ),

        logout: rxMethod<void>(
          pipe(
            switchMap(() => {
              return authApiService.logout().pipe(
                catchError(() => {
                  return EMPTY;
                }),
                finalize(() => {
                  store.clearAuth();
                  store._authNavigationService.navigateAfterLogout();
                }),
              );
            }),
          ),
        ),
      };
    }),
    withHooks((store) => ({
      onInit: () => {
        effect(() => {
          const user = store.user();
          const wasLoading =
            store.isLoginLoading() ||
            store.isRegisterLoading() ||
            store.isSocialLoginLoading();

          if (user && !wasLoading && store.navigate()) {
            store.navigateAfterLogin();
            store.resetNavigationFlag();
          }
        });

        effect(() => {
          const userResourceValue = store.userResource.value();

          if (userResourceValue) {
            store.setUser(userResourceValue);
          }
        });
      },
    })),
    withStorageSync({
      key: 'auth',
      select: (store) => ({
        user: store.user,
        current_team: store.current_team,
      }),
    }),
  );
}
